import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import { useGetTransactionsQuery } from '@/api/v1';
import DepositIcon from '@/assets/icons/deposit-icon.svg?react';
import TransferIcon from '@/assets/icons/send-icon.svg?react';
import InternalTransferIcon from '@/assets/icons/switch.svg?react';
import WithdrawIcon from '@/assets/icons/withdraw-icon.svg?react';
import { DisableInProduction } from '@/components/atoms/DisableInProduction';
import { HomeNavCard } from '@/components/molecules/HomeNavCard';
import { AwaitingFundingBanner } from '@/components/organisms/AwaitingFundingBanner';
import { BalanceLayout } from '@/components/organisms/BalanceLayout';
import { MainScreen } from '@/components/organisms/MainScreen';
import { QuoteWidget } from '@/components/organisms/QuoteWidget';
import {
  FF_DASHBOARD_NEW_BRL1_BALANCES,
  FF_DASHBOARD_NEW_INTERNAL_TRANSFERS,
  FF_DASHBOARD_NEW_MXNB_BALANCES,
} from '@/data/featureFlags';
import { useBalance } from '@/hooks/useBalance';
import { useCustomer } from '@/hooks/useCustomer';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { usePermissionsManager } from '@/hooks/usePermissionsManager';
import { useTranslator } from '@/hooks/useTranslator';
import { DetailedBalance } from '@/types/bankInfo';
import { CurrencyCodeType } from '@/types/currency';
import { PaymentFlowType } from '@/types/paymentFlowType';
import { Permission } from '@/types/permissions';

export const Home: React.FC = (): React.ReactElement => {
  const tr = useTranslator();
  const customer = useCustomer();
  const navigate = useNavigate();
  const { userIsAuthorizedTo } = usePermissionsManager();

  const isInternalTransfersEnabled = useFeatureFlag(FF_DASHBOARD_NEW_INTERNAL_TRANSFERS);

  const { data: awaitingFundingPage } = useGetTransactionsQuery(
    { awaitingFunding: true, size: 1, page: 1 },
    { skip: !customer.beneficiaryId },
  );
  const awaitingFundingCount = awaitingFundingPage?.totalElements ?? 0;

  const goToAwaitingFunding = React.useCallback((): void => {
    navigate('/activity?awaitingFunding=true');
  }, [navigate]);

  const isMxnbBalancesEnabled = useFeatureFlag(FF_DASHBOARD_NEW_MXNB_BALANCES);
  const isBrl1BalancesEnabled = useFeatureFlag(FF_DASHBOARD_NEW_BRL1_BALANCES);

  const usdBalance = useBalance(CurrencyCodeType.USD);
  const usdcBalance = useBalance(CurrencyCodeType.USDC);
  const usdtBalance = useBalance(CurrencyCodeType.USDT);
  const mxnbBalance = useBalance(CurrencyCodeType.MXNB, {
    skip: !isMxnbBalancesEnabled,
  });
  const brl1Balance = useBalance(CurrencyCodeType.BRL1, {
    skip: !isBrl1BalancesEnabled,
  });
  const balances = React.useMemo(
    (): DetailedBalance[] => [
      usdBalance,
      usdcBalance,
      usdtBalance,
      ...(isMxnbBalancesEnabled ? [mxnbBalance] : []),
      ...(isBrl1BalancesEnabled ? [brl1Balance] : []),
    ],
    [
      isMxnbBalancesEnabled,
      isBrl1BalancesEnabled,
      usdBalance,
      usdcBalance,
      usdtBalance,
      mxnbBalance,
      brl1Balance,
    ],
  );

  const screenTitle = React.useMemo((): string => {
    if (customer.firstName === '') {
      return tr('Welcome back');
    } else {
      return tr('WELCOME_TITLE', customer.firstName);
    }
  }, [customer.firstName, tr]);

  return (
    <MainScreen title={screenTitle}>
      <Box display="flex" flex={1} minHeight={0} width="100%" height="auto" gap={3} pt="22px">
        <Box sx={styles.balanceWidget}>
          {awaitingFundingCount > 0 && (
            <AwaitingFundingBanner count={awaitingFundingCount} onClick={goToAwaitingFunding} />
          )}
          <Paper sx={styles.balancePaper} elevation={1}>
            <BalanceLayout
              balances={balances}
              beneficiaryId={customer.beneficiaryId ?? undefined}
            />
          </Paper>
          {userIsAuthorizedTo(Permission.CREATE_TRANSACTIONS) && (
            <Box sx={styles.cards}>
              <HomeNavCard
                title={tr('TRANSFER_ACTION')}
                link={PaymentFlowType.transfer}
                icon={TransferIcon}
              />
              {isInternalTransfersEnabled && (
                <HomeNavCard
                  title={tr('INTERNAL_TRANSFER_ACTION')}
                  link={PaymentFlowType.internalTransfer}
                  icon={InternalTransferIcon}
                />
              )}
              <HomeNavCard
                title={tr('ADD_FUNDS')}
                link={PaymentFlowType.deposit}
                icon={DepositIcon}
              />
              <HomeNavCard
                title={tr('WITHDRAW')}
                link={PaymentFlowType.withdraw}
                icon={WithdrawIcon}
              />
            </Box>
          )}
        </Box>
        {userIsAuthorizedTo(Permission.CREATE_TRANSACTIONS) && (
          <DisableInProduction>
            <Paper sx={[styles.widget, styles.quoteWidget]} elevation={1}>
              <QuoteWidget />
            </Paper>
          </DisableInProduction>
        )}
      </Box>

      <Outlet />
    </MainScreen>
  );
};

const styles: Record<string, SystemStyleObject> = {
  cards: {
    display: 'flex',
    gap: 3,
    alignItems: 'center',
    width: '100%',
  },
  balanceWidget: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    flexBasis: 650,
    flexShrink: 0,
  },
  balancePaper: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'background.paper',
  },
  quoteWidget: {
    flexBasis: 350,
    flexShrink: 0,
    maxHeight: 'auto',
    display: {
      sm: 'none',
      md: 'block',
    },
  },
  widget: {
    height: 'min-content',
    backgroundColor: 'background.paper',
  },
};
